import React, { useState } from "react";
import {
  Table,
  Button,
  Popconfirm,
  Space,
  Tag,
  Checkbox,
  Modal,
  Spin,
  Form,
  Input,
  message,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import { canEdit, MODULES } from "../../utils/rbac";
import dayjs from "dayjs";
import axiosInstance from "../../configs";
import "../../styles/modern-pagination.css";

export default function InvoiceTable({
  dataSource,
  page,
  rowsPerPage,
  totalData,
  loading,
  handleDelete,
  onPageChange,
}) {
  const history = useHistory();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pdfModal, setPdfModal] = useState({
    visible: false,
    url: null,
    loading: false,
  });

  const onSelectChange = (newSelectedRowKeys) => {
    console.log("Selected Keys Changed:", newSelectedRowKeys);
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    type: "checkbox",
    selectedRowKeys,
    onChange: onSelectChange,
    columnWidth: 50,
    fixed: true,
    getCheckboxProps: (record) => ({
      name: record.invoiceNumber,
    }),
  };

  const handleViewPDF = async (id) => {
    setPdfModal({ visible: true, url: null, loading: true });

    try {
      const response = await axiosInstance.get(
        `/finance/sales-invoices/${id}/pdf`,
        {
          responseType: "blob",
        },
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      setPdfModal({ visible: true, url, loading: false });
    } catch (error) {
      console.error("Error loading PDF:", error);
      Modal.error({
        title: "Gagal Memuat PDF",
        content:
          "Terjadi kesalahan saat memuat invoice PDF. Silakan coba lagi.",
      });
      setPdfModal({ visible: false, url: null, loading: false });
    }
  };

  const handleClosePDF = () => {
    if (pdfModal.url) {
      URL.revokeObjectURL(pdfModal.url);
    }
    setPdfModal({ visible: false, url: null, loading: false });
  };

  const handleBulkPDF = async () => {
    setPdfModal({ visible: true, url: null, loading: true });

    try {
      const ids = selectedRowKeys.join(",");
      const response = await axiosInstance.get(
        `/finance/sales-invoices/bulk-pdf?ids=${ids}`,
        {
          responseType: "blob",
        },
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      setPdfModal({ visible: true, url, loading: false });
    } catch (error) {
      console.error("Error loading bulk PDF:", error);
      Modal.error({
        title: "Gagal Memuat PDF",
        content:
          "Terjadi kesalahan saat memuat bulk invoice PDF. Silakan coba lagi.",
      });
      setPdfModal({ visible: false, url: null, loading: false });
    }
  };

  // Email Logic
  const [emailModal, setEmailModal] = useState({
    visible: false,
    id: null,
    loading: false,
  });
  const [emailForm] = Form.useForm();

  const handleEmailClick = (record) => {
    setEmailModal({ visible: true, id: record.id, loading: false });
    // Default values
    emailForm.setFieldsValue({
      to: "", // Could be pre-filled if logic existed
      subject: `Tagihan Invoice ${record.invoiceNumber}`,
      body: `Yth, ${record.customer_name || "Bapak/Ibu"}

        Terlampir kami kirimkan invoice ${record.invoiceNumber}.
        Mohon dapat segera diproses.

        Terima kasih,
        PT Pasifik Logistik Berikat`,
    });
  };

  const handleSendEmail = async () => {
    try {
      const values = await emailForm.validateFields();
      setEmailModal((prev) => ({ ...prev, loading: true }));

      await axiosInstance.post(
        `/finance/sales-invoices/${emailModal.id}/send-email`,
        values,
      );

      message.success("Email berhasil dikirim!");
      setEmailModal({ visible: false, id: null, loading: false });

      // Refresh table to update status
      // We assume parent passes a refresh handler or we toggle a timestamp?
      // Currently page/rowPerPage changes trigger refresh.
      // We can reload by calling window.location or using a passed prop?
      // Since we don't have a callback prop explicitly named 'refresh', we might need to modify parent.
      // BUT: handleDelete calls fetchTableData(). This component receives 'handleDelete' but maybe not 'fetchTableData'.
      // Wait, InvoiceTable is a child. It accepts 'handleDelete' but not 'onRefresh'.
      // Ideally we accept onRefresh. For now, we manually reload page or ignore functionality update?
      // Actually, modifying status locally is tricky without refresh.
      // Let's reload window or ask user to refresh.
      window.location.reload();
    } catch (err) {
      console.error(err);
      message.error(
        "Gagal mengirim email: " + (err.response?.data?.error || err.message),
      );
      setEmailModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const columns = [
    {
      title: "JENIS DOKUMEN",
      dataIndex: "documentName",
      key: "documentName",
      width: 160,
    },
    {
      title: "NOPEN",
      dataIndex: "documentNumber",
      key: "documentNumber",
      width: 100,
    },
    {
      title: "TANGGAL NOPEN",
      dataIndex: "documentDate",
      key: "documentDate",
      width: 130,
      render: (date) => (date ? dayjs(date).format("DD MMMM YYYY") : "-"),
    },
    {
      title: "NOMOR INVOICE",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
      width: 150,
    },
    {
      title: "TANGGAL INVOICE",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      width: 130,
      render: (date) => (date ? dayjs(date).format("DD MMMM YYYY") : "-"),
    },
    {
      title: "STATUS",
      key: "keterangan",
      width: 140,
      render: (_, record) => {
        const statusMap = {
          DRAFT: { color: "orange", text: "Belum Dicetak" },
          READY: { color: "blue", text: "Siap Dicetak" },
          UNPAID: { color: "orange", text: "Belum Dibayar" },
          PAID: { color: "green", text: "Sudah Dibayar" },
        };
        const s = statusMap[record.status] || {
          color: "default",
          text: record.status || "-",
        };
        return <Tag color={s.color}>{s.text}</Tag>;
      },
    },
    {
      title: "KETERANGAN",
      key: "keterangan",
      width: 140,
      render: (_, record) => {
        const map = {
          DRAFT: { text: "Lengkapi Invoice", color: "#ff4d4f" }, // Red
          READY: { text: "Siap Cetak", color: "#1890ff" }, // Blue
          UNPAID: { text: "Menunggu Pembayaran", color: "#fa8c16" }, // Orange
          PAID: { text: "Lunas", color: "#52c41a" }, // Green
        };
        const item = map[record.status] || { text: "-", color: "black" };
        return (
          <span style={{ color: item.color, fontWeight: 500 }}>
            {item.text}
          </span>
        );
      },
    },
    {
      title: "AKSI",
      key: "action",
      width: 120,
      fixed: "right",
      render: (text, record) => (
        <Space size="small">
          <Button
            size="small"
            type="text"
            icon={<EyeOutlined style={{ color: "#1890ff" }} />}
            onClick={() => handleViewPDF(record.id)}
            title="Lihat Invoice PDF"
          />
          {/* Email Button - Only for READY, UNPAID, PAID (Not DRAFT) */}
          {record.status !== "DRAFT" && (
            <Button
              size="small"
              type="text"
              icon={<MailOutlined style={{ color: "green" }} />}
              onClick={() => handleEmailClick(record)}
              title="Kirim Invoice via Email"
            />
          )}
          {canEdit(MODULES.FINANCE) && (
            <>
              <Button
                size="small"
                type="primary"
                icon={<EditOutlined />}
                onClick={() => history.push(`/finance/edit/${record.id}`)}
                title="Edit Invoice"
              />
              <Popconfirm
                title="Are you sure to delete this invoice?"
                onConfirm={() => handleDelete(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  size="small"
                  danger
                  type="text"
                  icon={<DeleteOutlined />}
                  title="Hapus Invoice"
                />
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      {selectedRowKeys.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Checkbox
            checked={selectedRowKeys.length === dataSource.length}
            indeterminate={
              selectedRowKeys.length > 0 &&
              selectedRowKeys.length < dataSource.length
            }
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedRowKeys(dataSource.map((item) => item.id));
              } else {
                setSelectedRowKeys([]);
              }
            }}
          >
            Check Semua
          </Checkbox>
          <Button style={{ marginLeft: 16 }} onClick={handleBulkPDF}>
            Cetak Semua ({selectedRowKeys.length})
          </Button>
        </div>
      )}
      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={dataSource}
        rowKey={(record) => record.id}
        loading={loading}
        pagination={{
          current: page + 1,
          pageSize: rowsPerPage,
          total: totalData,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} items`,
          className: "modern-pagination",
          showQuickJumper: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          locale: {
            jump_to: "Halaman",
            page: "",
          },
          onChange: (newPage, newPageSize) => {
            console.log("Pagination changed to page:", newPage);
            if (onPageChange) {
              onPageChange(newPage, newPageSize);
            }
          },
          onShowSizeChange: (current, size) => {
            console.log("Page size changed to:", size);
            if (onPageChange) {
              onPageChange(1, size);
            }
          },
        }}
        scroll={{ x: 1200 }}
      />

      {/* PDF Preview Modal */}
      <Modal
        title="Preview Invoice"
        open={pdfModal.visible}
        onCancel={handleClosePDF}
        width="90%"
        style={{ top: 20 }}
        footer={[
          <Button key="close" onClick={handleClosePDF}>
            Tutup
          </Button>,
          pdfModal.url && (
            <Button
              key="print"
              type="primary"
              onClick={() => {
                const iframe = document.getElementById("invoice-pdf-iframe");
                if (iframe) {
                  iframe.contentWindow.print();
                }
              }}
            >
              Print
            </Button>
          ),
        ]}
      >
        {pdfModal.loading ? (
          <div style={{ textAlign: "center", padding: "50px 0" }}>
            <Spin size="large" />
            <p style={{ marginTop: 16 }}>Memuat invoice...</p>
          </div>
        ) : pdfModal.url ? (
          <iframe
            id="invoice-pdf-iframe"
            src={pdfModal.url}
            style={{ width: "100%", height: "80vh", border: "none" }}
            title="Invoice PDF"
          />
        ) : null}
      </Modal>

      {/* Email Compose Modal */}
      <Modal
        title="Kirim Invoice via Email"
        open={emailModal.visible}
        onCancel={() =>
          setEmailModal({ visible: false, id: null, loading: false })
        }
        onOk={handleSendEmail}
        confirmLoading={emailModal.loading}
        okText="Kirim"
        cancelText="Batal"
      >
        <Form form={emailForm} layout="vertical">
          <Form.Item
            name="to"
            label="Penerima (Email)"
            rules={[
              { required: true, message: "Harap isi email penerima" },
              { type: "email", message: "Format email tidak valid" },
            ]}
          >
            <Input placeholder="contoh@email.com" />
          </Form.Item>
          <Form.Item
            name="subject"
            label="Subjek"
            rules={[{ required: true, message: "Harap isi subjek" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="body"
            label="Pesan"
            rules={[{ required: true, message: "Harap isi pesan" }]}
          >
            <Input.TextArea rows={6} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
